// React context for saved graph management.
// Wraps the pure-function helpers in lib/graphs.ts with React state
// and exposes them through a provider + hook.

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { SavedGraph, AccumulatedGraph } from '../types';
import {
  loadSavedGraphs,
  saveSavedGraphs,
  createSavedGraph as createSavedGraphFn,
  deleteSavedGraph as deleteSavedGraphFn,
  renameSavedGraph as renameSavedGraphFn,
  updateSavedGraphData as updateSavedGraphDataFn,
  clearAllSavedGraphs as clearAllSavedGraphsFn,
  deriveGraphTitle,
} from '../lib/graphs';

export interface GraphStorageContextValue {
  savedGraphs: SavedGraph[];
  activeGraphId: string | null;
  saveGraph: (graph: AccumulatedGraph, title?: string, seedEntity?: string, seedLabel?: string) => string;
  deleteGraph: (id: string) => void;
  renameGraph: (id: string, title: string) => void;
  updateGraphData: (id: string, graph: AccumulatedGraph) => void;
  setActiveGraphId: (id: string | null) => void;
  clearAllGraphs: () => void;
}

const GraphStorageContext = createContext<GraphStorageContextValue | null>(null);

export function GraphStorageProvider({ children }: { children: React.ReactNode }) {
  const [savedGraphs, setSavedGraphs] = useState<SavedGraph[]>(() => loadSavedGraphs());
  const [activeGraphId, setActiveGraphId] = useState<string | null>(null);

  // Persist saved graphs whenever they change.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveSavedGraphs(savedGraphs);
  }, [savedGraphs]);

  const saveGraph = useCallback(
    (graph: AccumulatedGraph, title?: string, seedEntity?: string, seedLabel?: string): string => {
      const resolvedTitle = title || deriveGraphTitle(graph, seedEntity);
      const saved = createSavedGraphFn(graph, resolvedTitle, seedEntity, seedLabel);
      setSavedGraphs((prev) => [saved, ...prev]);
      setActiveGraphId(saved.id);
      return saved.id;
    },
    [],
  );

  const deleteGraph = useCallback(
    (id: string) => {
      setSavedGraphs((prev) => deleteSavedGraphFn(prev, id));
      setActiveGraphId((prev) => (prev === id ? null : prev));
    },
    [],
  );

  const renameGraph = useCallback((id: string, title: string) => {
    setSavedGraphs((prev) => renameSavedGraphFn(prev, id, title));
  }, []);

  const updateGraphData = useCallback((id: string, graph: AccumulatedGraph) => {
    setSavedGraphs((prev) => updateSavedGraphDataFn(prev, id, graph));
  }, []);

  const clearAllGraphs = useCallback(() => {
    clearAllSavedGraphsFn();
    setSavedGraphs([]);
    setActiveGraphId(null);
  }, []);

  const value: GraphStorageContextValue = {
    savedGraphs,
    activeGraphId,
    saveGraph,
    deleteGraph,
    renameGraph,
    updateGraphData,
    setActiveGraphId,
    clearAllGraphs,
  };

  return (
    <GraphStorageContext.Provider value={value}>{children}</GraphStorageContext.Provider>
  );
}

export function useGraphStorageContext(): GraphStorageContextValue {
  const ctx = useContext(GraphStorageContext);
  if (!ctx) {
    throw new Error('useGraphStorageContext must be used within a GraphStorageProvider');
  }
  return ctx;
}
